import { useTheme } from "@repo/tokens";
import {
  Box,
  HStack,
  Icon,
  RippleButton,
  Spinner,
  Text,
  VStack,
} from "@repo/ui";
import { TestModal } from "../TestModal";
import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { ExperienceRating } from "../../hooks/useSubmitFeedback";
import type { RateExperienceDrawerProps } from "./types";
import styles from "./RateExperienceDrawer.module.scss";

const EXPERIENCE_MAP: Record<string, ExperienceRating> = {
  veryBad: "VERY_BAD",
  bad: "BAD",
  average: "AVERAGE",
  good: "GOOD",
  veryGood: "VERY_GOOD",
};

export const RateExperienceDrawer = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}: RateExperienceDrawerProps) => {
  const { theme } = useTheme();
  const { t } = useTranslation("tests");

  const [selected, setSelected] = useState<
    "veryBad" | "bad" | "average" | "good" | "veryGood"
  >();

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [otherText, setOtherText] = useState("");

  const options = useMemo(
    () =>
      [
        {
          id: "veryBad",
          icon: "emojiVerySad",
          label: t("rate_experience.very_bad"),
        },
        { id: "bad", icon: "emojiSad", label: t("rate_experience.bad") },
        {
          id: "average",
          icon: "emojiAverage",
          label: t("rate_experience.average"),
        },
        { id: "good", icon: "emojiGood", label: t("rate_experience.good") },
        {
          id: "veryGood",
          icon: "emojiVeryGood",
          label: t("rate_experience.very_good"),
        },
      ] as const,
    [t],
  );

  const othersLabel = t("rate_experience.tags.others");

  const tags = useMemo(
    () => [
      t("rate_experience.tags.clear_questions"),
      t("rate_experience.tags.balanced_difficulty"),
      t("rate_experience.tags.relevant_questions"),
      t("rate_experience.tags.no_issues"),
      t("rate_experience.tags.easy_questions"),
      othersLabel,
    ],
    [t, othersLabel],
  );

  const isOthersSelected = selectedTags.includes(othersLabel);

  const handleTagClick = useCallback(
    (tag: string) => {
      setSelectedTags((prev) =>
        prev.includes(tag)
          ? prev.filter((item) => item !== tag)
          : [...prev, tag],
      );
      if (tag === othersLabel && isOthersSelected) {
        setOtherText("");
      }
    },
    [othersLabel, isOthersSelected],
  );

  const renderBody = () => {
    return (
      <VStack gap="lg" className={styles.body}>
        <HStack
          justifyContent="space-between"
          align="center"
          className={styles.header}
        >
          <Text className={styles.title}>{t("rate_experience.title")}</Text>

          <RippleButton
            variant="text"
            shape="round"
            size="md"
            iconOnly
            onPress={onClose}
          >
            <Icon name="close" size={24} color={theme.colors.primary[40]} />
          </RippleButton>
        </HStack>

        <HStack
          justifyContent="space-between"
          align="center"
          className={styles.emojiRow}
        >
          {options.map((item) => {
            const isActive = selected === item.id;

            return (
              <Box
                key={item.id}
                onClick={() => setSelected(item.id)}
                className={styles.emojiItem}
              >
                <VStack
                  align="center"
                  gap="xs"
                  className={`${styles.emojiBox} ${isActive ? styles.emojiBoxActive : ""}`}
                >
                  <Icon name={item.icon} height={32} width={32} />
                  <Text
                    variant="body"
                    weight="emphasized"
                    color={theme.colors.neutral[50]}
                    align="center"
                    className={styles.emojiLabel}
                  >
                    {item.label}
                  </Text>
                </VStack>
              </Box>
            );
          })}
        </HStack>

        {selected && (
          <>
            <Text className={styles.title}>
              {t("rate_experience.subtitle")}
            </Text>

            <HStack flexWrap="wrap" gap="sm">
              {tags.map((tag) => {
                const isActive = selectedTags.includes(tag);

                return (
                  <RippleButton
                    key={tag}
                    onClick={() => handleTagClick(tag)}
                    shape="round"
                    size="md"
                    variant={isActive ? "primary" : "toggle"}
                    height={48}
                  >
                    <Text
                      className={styles.chipTitle}
                      color={
                        isActive ? "colors.neutral.100" : "colors.primary.10"
                      }
                    >
                      {tag}
                    </Text>
                  </RippleButton>
                );
              })}
            </HStack>

            {isOthersSelected && (
              <Box className={styles.otherInputWrapper}>
                <Text className={styles.otherFloatingLabel}>{othersLabel}</Text>
                <input
                  placeholder={t("rate_experience.other_placeholder")}
                  className={styles.otherInput}
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  onFocus={(e) => {
                    const target = e.currentTarget;
                    setTimeout(() => {
                      target.scrollIntoView({
                        block: "center",
                        behavior: "smooth",
                      });
                    }, 300);
                  }}
                />
              </Box>
            )}

            <Box className={styles.submitWrapper}>
              <RippleButton
                onPress={() => {
                  if (selected && onSubmit) {
                    const feedback = isOthersSelected ? otherText.trim() : "";
                    onSubmit(
                      EXPERIENCE_MAP[selected],
                      selectedTags,
                      feedback || undefined,
                    );
                  }
                }}
                disabled={
                  isSubmitting ||
                  selectedTags.length === 0 ||
                  (isOthersSelected && otherText.trim().length === 0)
                }
                size="md"
                fullWidth
              >
                {isSubmitting ? (
                  <Spinner size="sm" />
                ) : (
                  <>{t("rate_experience.submit")}</>
                )}
              </RippleButton>
            </Box>
          </>
        )}
      </VStack>
    );
  };

  return <TestModal isOpen={isOpen} onClose={onClose} body={renderBody()} />;
};
